/* eslint-disable react-refresh/only-export-components -- DEFAULT_OPTIONS, WheelOptions and FONT_STACK_MAP are co-exported with the component by design */
import React, { useEffect } from 'react';
import { X, Volume2, VolumeX, Trash2, Clock, Zap, Bold, CaseSensitive, ALargeSmall, Scale, Palette, Circle, Monitor } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { PALETTES, PALETTE_NAMES, type PaletteName } from '../utils/palettes';
import CenterImageControl from './CenterImageControl';
import { useLang } from '../utils/i18n';
export type { PaletteName };

export interface WheelOptions {
  autoRemove: boolean;
  soundEnabled: boolean;
  spinDuration: 'slow' | 'normal' | 'fast';
  showConfetti: boolean;
  /**
   * How the winner is presented after a spin.
   *
   * 'modal' — full-screen dialog with confetti, focus trap, and action buttons.
   * 'banner' — non-blocking overlay on the wheel card that auto-dismisses
   *   after 3 seconds, letting the teacher spin again immediately.
   */
  winnerDisplay: 'modal' | 'banner';
  fontFamily: 'inter' | 'georgia' | 'courier' | 'impact' | 'trebuchet';
  fontWeight: 'normal' | 'bold';
  fontSize: 'auto' | 'small' | 'medium' | 'large';
  /**
   * Draw slices proportional to entry weight. Turning it off draws an equal
   * split while selection stays weighted — the odds are unchanged, they are
   * just no longer visible in the geometry.
   */
  weightedSegments: boolean;
  /**
   * Palette used for segments the user has not coloured individually. A
   * per-entry colour always wins over this — see WheelEntry.color.
   */
  palette: PaletteName;
  /** Rim drawn around the wheel. 'none' is a hairline, not a missing border. */
  rimStyle: 'classic' | 'bold' | 'none';
  /** Optional locally uploaded image rendered in the wheel's center hub. */
  centerImageDataUrl?: string;
}

export const DEFAULT_OPTIONS: WheelOptions = {
  autoRemove: false,
  soundEnabled: true,
  spinDuration: 'normal',
  showConfetti: true,
  winnerDisplay: 'modal',
  // On by default. While every weight is 1 this is indistinguishable from an
  // equal split, so it only starts mattering once someone changes a weight —
  // at which point showing the real odds is the honest default.
  weightedSegments: true,
  palette: 'pastel',
  rimStyle: 'classic',
  fontFamily: 'inter',  // matches the existing look
  fontWeight: 'bold',   // existing labels are bold
  fontSize: 'auto',     // existing auto-scaling behavior
};

/**
 * Canvas font stacks. 'inter' leads with 'Inter Variable' because that is the
 * family name registered by the self-hosted @fontsource-variable/inter package —
 * plain "Inter" resolves to nothing and would silently fall back to system-ui.
 */
export const FONT_STACK_MAP: Record<WheelOptions['fontFamily'], string> = {
  inter: "'Inter Variable', Inter, system-ui, sans-serif",
  georgia: 'Georgia, "Times New Roman", serif',
  courier: '"Courier New", Courier, monospace',
  impact: 'Impact, "Arial Narrow", sans-serif',
  trebuchet: '"Trebuchet MS", Tahoma, sans-serif',
};

const FONT_OPTIONS: { value: WheelOptions['fontFamily']; label: string }[] = [
  { value: 'inter', label: 'Inter' },
  { value: 'georgia', label: 'Georgia' },
  { value: 'courier', label: 'Courier' },
  { value: 'impact', label: 'Impact' },
  { value: 'trebuchet', label: 'Trebuchet' },
];

interface OptionsModalProps {
  options: WheelOptions;
  onChange: (opts: WheelOptions) => void;
  onClose: () => void;
  /** Number of entries carrying a custom colour — 0 hides the reset control. */
  customColorCount?: number;
  /** Clear every per-entry colour and fall back to the auto palette. */
  onResetColors?: () => void;
}

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}> = ({ checked, onChange, label, description, icon }) => (
  <label className="flex items-start gap-3 cursor-pointer group">
    <div className="flex-shrink-0 mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white">{label}</p>
      {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 ${
        checked
          ? 'bg-violet-500 border border-transparent'
          : 'bg-gray-200 dark:bg-gray-600 border border-gray-400 dark:border-transparent'
      }`}
    >
      <span
        className={`absolute top-0.5 start-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
          checked ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </label>
);

const OptionsModal: React.FC<OptionsModalProps> = ({ options, onChange, onClose, customColorCount = 0, onResetColors }) => {
  const { t } = useLang();
  const paletteLabels: Record<PaletteName, string> = {
    classic: t('options.paletteClassic'),
    pastel: t('options.palettePastel'),
    corporate: t('options.paletteCorporate'),
    sunset: t('options.paletteSunset'),
    forest: t('options.paletteForest'),
    slate: t('options.paletteSlate'),
  };
  const set = <K extends keyof WheelOptions>(key: K, val: WheelOptions[K]) =>
    onChange({ ...options, [key]: val });

  const setCenterImage = (imageDataUrl: string | undefined) => {
    const next = { ...options };
    if (imageDataUrl) next.centerImageDataUrl = imageDataUrl;
    else delete next.centerImageDataUrl;
    onChange(next);
  };

  // WCAG 2.1.2 + 2.4.3 — trap focus inside the modal, restore on close
  const trapRef = useFocusTrap(true);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal — WCAG dialog pattern */}
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="options-modal-title"
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-winner-pop"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 id="options-modal-title" className="font-bold text-gray-900 dark:text-white text-base">{t('options.title')}</h2>
          <button
            onClick={onClose}
            aria-label={t('options.close')}
            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-5 max-h-[65vh] overflow-y-auto">
          <Toggle
            checked={options.soundEnabled}
            onChange={(v) => set('soundEnabled', v)}
            label={t('options.sound')}
            description={t('options.soundDesc')}
            icon={options.soundEnabled ? <Volume2 size={18} className="text-violet-500" /> : <VolumeX size={18} className="text-gray-500 dark:text-gray-400" />}
          />

          <Toggle
            checked={options.autoRemove}
            onChange={(v) => set('autoRemove', v)}
            label={t('options.autoRemove')}
            description={t('options.autoRemoveDesc')}
            icon={<Trash2 size={18} className="text-gray-500 dark:text-gray-400" />}
          />

          <Toggle
            checked={options.showConfetti}
            onChange={(v) => set('showConfetti', v)}
            label={t('options.confetti')}
            description={t('options.confettiDesc')}
            icon={<Zap size={18} className="text-yellow-400" />}
          />

          {/* Winner display mode */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Monitor size={18} className="text-gray-500 dark:text-gray-400" />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('options.winnerDisplay')}</p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2.5">
              {t('options.winnerDisplayDesc')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(['modal', 'banner'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => set('winnerDisplay', mode)}
                  className={`py-2 rounded-xl text-xs font-semibold capitalize border-2 transition-all ${
                    options.winnerDisplay === mode
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {mode === 'modal' ? `🎉 ${t('options.modal')}` : `⚡ ${t('options.banner')}`}
                </button>
              ))}
            </div>
          </div>

          <Toggle
            checked={options.weightedSegments}
            onChange={(v) => set('weightedSegments', v)}
            label={t('options.weighted')}
            description={t('options.weightedDesc')}
            icon={<Scale size={18} className="text-gray-500 dark:text-gray-400" />}
          />

          {/* Reset colours — only shown when there is something to reset, so
              the panel does not carry a permanently dead control. */}
          {customColorCount > 0 && onResetColors && (
            <div className="flex items-start gap-3">
              <Palette size={18} className="flex-shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('options.segmentColors')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {t(customColorCount === 1 ? 'options.customColorOne' : 'options.customColorMany', { count: customColorCount })}
                </p>
              </div>
              <button
                type="button"
                onClick={onResetColors}
                className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors"
              >
                {t('options.reset')}
              </button>
            </div>
          )}

          {/* Segment palette */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <Palette size={18} className="text-gray-500 dark:text-gray-400" />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('options.palette')}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PALETTE_NAMES.map(name => (
                <button
                  key={name}
                  onClick={() => set('palette', name)}
                  aria-pressed={options.palette === name}
                  className={`flex items-center gap-2 py-2 px-2.5 rounded-xl border-2 transition-all ${
                    options.palette === name
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {/* Four swatches is enough to tell the palettes apart without
                      turning the panel into a colour chart. */}
                  <span className="flex flex-shrink-0" aria-hidden="true">
                    {PALETTES[name].slice(0, 4).map((c, i) => (
                      <span key={i} className="w-3 h-5 first:rounded-s-md last:rounded-e-md" style={{ backgroundColor: c }} />
                    ))}
                  </span>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 truncate">{paletteLabels[name]}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('options.paletteDesc')}
            </p>
          </div>

          <CenterImageControl
            value={options.centerImageDataUrl}
            onChange={setCenterImage}
          />

          {/* Rim */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <Circle size={18} className="text-gray-500 dark:text-gray-400" />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('options.rim')}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(['classic', 'bold', 'none'] as const).map(r => (
                <button
                  key={r}
                  onClick={() => set('rimStyle', r)}
                  className={`py-2 rounded-xl text-xs font-semibold capitalize border-2 transition-all ${
                    options.rimStyle === r
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {t(r === 'none' ? 'options.rimMinimal' : r === 'classic' ? 'options.rimClassic' : 'options.rimBold')}
                </button>
              ))}
            </div>
          </div>

          {/* Spin Duration */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <Clock size={18} className="text-gray-500 dark:text-gray-400" />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('options.duration')}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(['slow', 'normal', 'fast'] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => set('spinDuration', d)}
                  className={`py-2 rounded-xl text-xs font-semibold capitalize border-2 transition-all ${
                    options.spinDuration === d
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {t(d === 'slow' ? 'options.slow' : d === 'normal' ? 'options.normal' : 'options.fast')}
                </button>
              ))}
            </div>
          </div>

          {/* Label Font */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <CaseSensitive size={18} className="text-gray-500 dark:text-gray-400" />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('options.font')}</p>
            </div>
            <div className="flex flex-col gap-2">
              {FONT_OPTIONS.map(f => (
                <button
                  key={f.value}
                  onClick={() => set('fontFamily', f.value)}
                  className={`py-2 px-3 rounded-xl text-start text-sm border-2 transition-all truncate ${
                    options.fontFamily === f.value
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                  style={{ fontFamily: FONT_STACK_MAP[f.value] }}
                >
                  {f.label} — {t('options.fontPreview')}
                </button>
              ))}
            </div>
          </div>

          {/* Font Weight */}
          <Toggle
            checked={options.fontWeight === 'bold'}
            onChange={(v) => set('fontWeight', v ? 'bold' : 'normal')}
            label={t('options.boldLabels')}
            description={t('options.boldLabelsDesc')}
            icon={<Bold size={18} className="text-gray-500 dark:text-gray-400" />}
          />

          {/* Text Size */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <ALargeSmall size={18} className="text-gray-500 dark:text-gray-400" />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('options.textSize')}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(['small', 'auto', 'large'] as const).map(size => (
                <button
                  key={size}
                  onClick={() => set('fontSize', size)}
                  className={`py-2 rounded-xl text-xs font-semibold capitalize border-2 transition-all ${
                    options.fontSize === size
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {t(size === 'auto' ? 'options.auto' : size === 'small' ? 'options.small' : 'options.large')}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('options.textSizeDesc')}
            </p>
          </div>
        </div>

        <div className="px-5 pb-5">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-gray-900 dark:bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors text-sm"
          >
            {t('options.done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptionsModal;

